import React, { useState, useEffect } from 'react';
import { FiCheckCircle } from 'react-icons/fi';
import { Button } from '../ui/button';
import { supabase } from '../../supabase';

const PricingPlans = ({ onSelectPlan, ctaLabel, isDashboard = false }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAnnual, setIsAnnual] = useState(false);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data, error } = await supabase
          .from('subscription_plans')
          .select('*')
          .eq('is_active', true)
          .order('price', { ascending: true });

        if (error) throw error;

        let validPlans = data || [];

        const now = new Date();

        validPlans = validPlans.filter(plan => {
          // Check scheduling
          if (plan.valid_from && new Date(plan.valid_from) > now) {
             return false;
          }
          if (plan.valid_until && new Date(plan.valid_until) < now) {
             return false;
          }

          // Fallback logic if the plan is old and missing the new flags
          const hasPublicFlag = plan.show_on_public !== undefined && plan.show_on_public !== null;
          const hasDashboardFlag = plan.show_on_dashboard !== undefined && plan.show_on_dashboard !== null;

          if (isDashboard) {
             if (hasDashboardFlag) return plan.show_on_dashboard;
             return plan.visibility !== 'hidden';
          } else {
             if (hasPublicFlag) return plan.show_on_public;
             return plan.visibility === 'public';
          }
        });

        setPlans(validPlans);

      } catch (error) {
        console.error('Error fetching pricing plans:', error);
        // Fallback to hardcoded plans if DB fetch fails (optional, but good for resilience/dev)
        setPlans([
          {
            id: 'starter',
            name: 'Starter',
            description: 'Perfect for solopreneurs and small startups.',
            price: 49,
            interval: 'month',
            features: ['3 AI Agents', '10 Social Posts/mo', 'Basic Analytics', '1 User Seat'],
            stripe_price_id: 'price_1SbY60DFN5Ein5wKW4Z7Dhby'
          },
          {
            id: 'pro',
            name: 'Pro',
            description: 'For growing businesses scaling their marketing.',
            price: 149,
            interval: 'month',
            features: ['All 7 AI Agents', 'Unlimited Social Posts', 'Advanced Analytics', '5 User Seats', 'Priority Support'],
            stripe_price_id: 'price_1SbY6ZDFN5Ein5wKz44Ts2T2'
          },
          {
            id: 'agency',
            name: 'Agency',
            description: 'For agencies managing multiple client brands.',
            price: 499,
            interval: 'month',
            features: ['Unlimited Workspaces', 'White-label Reports', 'API Access', 'Unlimited Seats', 'Dedicated Account Manager'],
            stripe_price_id: 'price_1SbY6tDFN5Ein5wKwK63bk4r'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  if (loading) {
    return <div className="text-center py-12 text-charcoal-500">Loading pricing plans...</div>;
  }

  // If no plans found (and no fallback triggered or empty DB), show nothing or a message
  if (plans.length === 0) {
     return <div className="text-center py-12 text-charcoal-500">No pricing plans available at the moment.</div>;
  }

  const subscriptionPlans = plans.filter(p => p.gates?.type !== 'token_package');
  const tokenPackages = plans.filter(p => p.gates?.type === 'token_package');

  const renderPlanCard = (plan, isTokenPackage) => {
    const isPro = !isTokenPackage && (plan.is_popular || plan.name === 'Pro' || (plan.name && plan.name.includes('Professional')));
    const isAgency = !isTokenPackage && (plan.name === 'Agency' || (plan.name && plan.name.includes('Enterprise')));

    const containerClasses = isPro
      ? "bg-charcoal-900 dark:bg-charcoal-800 p-8 rounded-2xl border border-charcoal-900 dark:border-charcoal-700 shadow-xl relative transform scale-105 z-10"
      : "bg-white dark:bg-charcoal-900 p-8 rounded-2xl border border-charcoal-200 dark:border-charcoal-800 shadow-sm hover:shadow-xl transition-shadow relative";

    const titleClass = isPro ? "text-xl font-bold text-white mb-2" : "text-xl font-bold text-charcoal-900 dark:text-white mb-2";
    const descClass = isPro ? "text-charcoal-400 mb-6 text-sm" : "text-charcoal-500 dark:text-charcoal-400 mb-6 text-sm";
    const priceClass = isPro ? "text-4xl font-bold text-white mb-6" : "text-4xl font-bold text-charcoal-900 dark:text-white mb-6";
    const priceSpanClass = isPro ? "text-lg text-charcoal-500 font-normal" : "text-lg text-charcoal-400 dark:text-charcoal-500 font-normal";
    const checkIconClass = isPro ? "text-purple-400" : "text-green-500";
    const listItemClass = isPro ? "flex items-center gap-2 text-charcoal-300 text-sm" : "flex items-center gap-2 text-charcoal-700 dark:text-charcoal-300 text-sm";

    const displayPrice = (isAnnual && plan.annual_price && !isTokenPackage) ? plan.annual_price : plan.price;
    const intervalDisplay = isAnnual && plan.annual_price && !isTokenPackage ? 'month (billed annually)' : plan.interval;

    // Calculate savings
    const monthlyTotalForYear = plan.price * 12;
    const annualTotal = plan.annual_price * 12;
    const savings = monthlyTotalForYear - annualTotal;
    const hasSavings = isAnnual && plan.annual_price && savings > 0 && !isTokenPackage;

    return (
      <div key={plan.id} className={containerClasses}>
        {isPro && (
          <div className="absolute top-0 right-0 bg-gradient-to-r from-orange-500 to-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">POPULAR</div>
        )}
        <h3 className={titleClass}>{plan.name}</h3>
        <p className={descClass}>{plan.description}</p>

        <div className={priceClass}>
          ${displayPrice}
          {!isTokenPackage && <span className={priceSpanClass}>/{intervalDisplay}</span>}
        </div>
        {hasSavings && (
          <div className="text-green-500 text-sm font-semibold mb-4 bg-green-50 dark:bg-green-900/20 inline-block px-3 py-1 rounded-full">
            Save ${savings.toFixed(0)} a year
          </div>
        )}

        {!isTokenPackage && (
          <div className="mb-6 space-y-1">
            <div className={`font-semibold ${isPro ? "text-charcoal-300" : "text-charcoal-800 dark:text-charcoal-200"}`}>
              {plan.gates?.token_amount ? `${plan.gates.token_amount.toLocaleString()} seeds` : 'No seeds'}
            </div>
            <div className={`font-semibold ${isPro ? "text-charcoal-300" : "text-charcoal-800 dark:text-charcoal-200"}`}>
              Unlimited workspaces
            </div>
          </div>
        )}

        <p className={`font-semibold mb-2 mt-4 ${isPro ? "text-white" : "text-charcoal-900 dark:text-white"}`}>Features:</p>
        <ul className="space-y-4 mb-8">
          {Array.isArray(plan.features) && plan.features.map((feature, idx) => (
            <li key={idx} className={listItemClass}>
              <FiCheckCircle className={checkIconClass} />
              {feature === 'White-label Reports' || feature.includes('White-label') ? (
                <span>
                  {feature}
                  <span className="bg-blue-100 text-blue-600 text-[10px] px-1.5 py-0.5 rounded-full font-bold ml-2 uppercase tracking-wide">
                    Beta
                  </span>
                </span>
              ) : (
                feature
              )}
            </li>
          ))}
        </ul>
        <Button
          variant={isPro ? "default" : "outline"}
          className={isPro ? "w-full !bg-white !text-black !hover:bg-charcoal-50 dark:!bg-charcoal-200 dark:!text-black shadow-[0_0_20px_rgba(59,130,246,0.5)] hover:shadow-[0_0_25px_rgba(59,130,246,0.7)] border border-blue-500/20" : "w-full dark:text-white dark:border-charcoal-700 dark:hover:bg-charcoal-800"}
          onClick={() => onSelectPlan((isAnnual && plan.stripe_annual_price_id) ? plan.stripe_annual_price_id : plan.stripe_price_id, isTokenPackage ? 'payment' : 'subscription', plan.gates?.token_amount)}
        >
          {ctaLabel || (isTokenPackage ? `Buy ${plan.name}` : `Choose ${plan.name}`)}
        </Button>
        {plan.trial_days > 0 && (
          <div className="mt-4 text-center text-xs text-charcoal-500 dark:text-charcoal-400 font-medium">
            {plan.trial_days}-Day Free Trial
            {!plan.trial_cc_required ? ' (No CC Required)' : ''}
          </div>
        )}

        {plan.guarantee_enabled && plan.guarantee_days > 0 && (
          <div className="mt-2 text-center text-xs text-charcoal-500 dark:text-charcoal-400 font-medium">
            🛡️ {plan.guarantee_days}-Day Money Back Guarantee
          </div>
        )}
      </div>
    );
  };

  const hasAnnualOptions = subscriptionPlans.some(p => p.annual_price);

  return (
    <div>
      {hasAnnualOptions && !isDashboard && (
        <div className="flex justify-center items-center mb-10 gap-4">
          <span className={`text-sm font-medium ${!isAnnual ? 'text-charcoal-900 dark:text-white' : 'text-charcoal-500 dark:text-charcoal-400'}`}>Monthly</span>
          <button
            onClick={() => setIsAnnual(!isAnnual)}
            className="w-14 h-7 bg-indigo-100 dark:bg-charcoal-700 rounded-full relative transition-colors focus:outline-none"
            aria-label="Toggle annual billing"
          >
            <div className={`w-5 h-5 bg-indigo-600 rounded-full absolute top-1 transition-transform ${isAnnual ? 'translate-x-8' : 'translate-x-1'}`}></div>
          </button>
          <span className={`text-sm font-medium flex items-center gap-2 ${isAnnual ? 'text-charcoal-900 dark:text-white' : 'text-charcoal-500 dark:text-charcoal-400'}`}>
            Annually <span className="bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Save 20%</span>
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {subscriptionPlans.map(plan => renderPlanCard(plan, false))}
      </div>

      {tokenPackages.length > 0 && (
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8 text-center text-charcoal-900 dark:text-white">One-Time Purchases</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {tokenPackages.map(plan => renderPlanCard(plan, true))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPlans;
